import { use } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'use-intl';

import { SurveyThemeContext } from '@/shared-utilities';

const ThemedModal = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

interface Props {
  onClose: () => void;
}

export default function SurveyAiFormsInstructionsModal({ onClose }: Props) {
  const t = useTranslations('AIForms.instructions');
  const { input: themeInput, styles } = use(SurveyThemeContext);

  const themeSettings = themeInput?.themeSettings;
  const hasGradient = !!themeSettings?.bgColor?.includes('gradient');

  return (
    <>
      {/* Backdrop */}
      <div
        className='fixed inset-0 z-40 cursor-pointer bg-slate-950 opacity-40'
        onClick={onClose}
        aria-hidden='true'
        data-test='instructions-modal-backdrop'
      />

      {/* Modal */}
      <ThemedModal
        className='absolute top-0 right-20 z-50'
        themeStyles={styles}
        data-test='instructions-modal-content'
      >
        <div className='relative h-72 w-70 rounded-3xl border border-gray-200 bg-white p-2'>
          {/* Animation/Illustration Section */}
          <div className='flex gap-2 rounded-[22px] bg-gray-200 p-2'>
            {/* Left side - AI illustration */}
            <div
              className='flex h-26 grow basis-0 flex-col justify-between rounded-[20px] p-2 data-[has-gradient=false]:bg-(--brand-bg-color-only) data-[has-gradient=true]:bg-(image:--brand-bg-color-only)'
              data-has-gradient={hasGradient}
            >
              {/* Top toggles */}
              <div className='flex w-full items-center justify-between'>
                <div className='h-4 w-6 rounded-4xl bg-(--brand-primary) shadow-md' />
                <div className='flex h-4 w-6 items-center rounded-[48px] bg-(--brand-primary) p-0.5'>
                  <div className='size-3 rounded-4xl bg-white shadow-md' />
                </div>
              </div>

              {/* White bar (composer) */}
              <div className='h-4 w-full rounded-[20px] bg-white' />

              {/* Progress dots */}
              <div className='flex h-4 w-full items-end justify-center gap-1'>
                <div className='h-1.5 w-4 rounded-sm bg-(--brand-primary) shadow-lg' />
                <div className='size-1.5 rounded-sm bg-white opacity-60' />
                <div className='size-1.5 rounded-sm bg-white opacity-60' />
                <div className='size-1.5 rounded-sm bg-white opacity-60' />
                <div className='size-1.5 rounded-sm bg-white opacity-60' />
              </div>
            </div>

            {/* Right side - Form illustration */}
            <div className='flex h-26 w-20 flex-col gap-2 rounded-[20px] bg-white p-2'>
              {/* Question card 1 */}
              <div className='flex h-6 w-full flex-col gap-1 rounded-[20px] border border-gray-200 bg-white p-1.5'>
                <div className='h-1 w-full rounded-[20px] bg-gray-100' />
                <div className='h-1 w-10.75 rounded-[20px] bg-gray-100' />
              </div>

              {/* Question card 2 */}
              <div className='flex h-6 w-full flex-col gap-1 rounded-[20px] border border-gray-200 bg-white p-1.5'>
                <div className='h-1 w-8.75 rounded-[20px] bg-gray-100' />
                <div className='h-1 w-11.5 rounded-[20px] bg-gray-100' />
              </div>

              {/* Question card 3 */}
              <div className='flex h-6 w-full flex-col gap-1 rounded-[20px] border border-gray-200 bg-white p-1.5'>
                <div className='h-1 w-full rounded-[20px] bg-gray-100' />
                <div className='h-1 w-10.5 rounded-[20px] bg-gray-100' />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className='flex flex-col gap-2 p-4 text-black'>
            <p className='[font-family:var(--brand-font)] text-base/6 font-semibold'>
              {t('title')}
            </p>
            <div className='flex flex-col justify-center text-sm/5.5 opacity-60'>
              <p className='[font-family:var(--brand-font)]'>{t('body')}</p>
            </div>
          </div>

          {/* Close button */}
          <button
            type='button'
            onClick={onClose}
            className='absolute top-4 left-61 size-5 cursor-pointer text-black transition-colors hover:opacity-70'
            aria-label={t('closeAria')}
            data-test='instructions-modal-close'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
            >
              <line x1='15' y1='5' x2='5' y2='15' />
              <line x1='5' y1='5' x2='15' y2='15' />
            </svg>
          </button>
        </div>
      </ThemedModal>
    </>
  );
}
